import React, { createContext, useContext, ReactNode } from 'react';
import { useSiteConfiguration } from '@/hooks/useSiteConfiguration';
import type { SiteConfig } from '@/integrations/supabase/types';

interface SiteConfigContextValue {
  configuration: ReturnType<typeof useSiteConfiguration>['configuration'];
  isLoading: boolean;
  error: any;
}

const SiteConfigContext = createContext<SiteConfigContextValue | undefined>(undefined);

interface SiteConfigProviderProps {
  children: ReactNode;
  subdominio?: string;
}

/**
 * Provider para configurações do site
 * Centraliza o acesso às configurações em toda a aplicação
 */
export function SiteConfigProvider({ children, subdominio = 'desordibroker' }: SiteConfigProviderProps) {
  const { configuration, isLoading, error } = useSiteConfiguration(subdominio);

  return (
    <SiteConfigContext.Provider value={{ configuration, isLoading, error }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

/**
 * Hook para acessar configurações do site em qualquer componente
 */
export function useSiteConfigContext() {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfigContext must be used within a SiteConfigProvider');
  }
  return context;
}

/**
 * HOC para injetar configurações em componentes
 */
export function withSiteConfig<P extends object>(
  Component: React.ComponentType<P & { siteConfig: SiteConfigContextValue['configuration'] }>
) {
  return function WithSiteConfigComponent(props: P) {
    const { configuration } = useSiteConfigContext();
    return <Component {...props} siteConfig={configuration} />;
  };
}
